import React, { useState } from 'react';
import {
    Modal,
    Button,
    FormField,
    SpaceBetween,
    Alert
} from "@cloudscape-design/components";

interface UploadModalProps {
    visible: boolean;
    onDismiss: () => void;
    onUpload: (jsonData: any) => void;
}

const UploadModal: React.FC<UploadModalProps> = ({ visible, onDismiss, onUpload }) => {
    const [jsonData, setJsonData] = useState<any>(null);
    const [fileName, setFileName] = useState<string>('');
    const [error, setError] = useState<string>('');

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setFileName(file.name);
            setError('');
            
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const json = JSON.parse(e.target?.result as string);
                    setJsonData(json);
                    setError('');
                } catch (error) {
                    setError('Invalid JSON file. Please check the file format.');
                    setJsonData(null);
                }
            };
            
            reader.onerror = () => {
                setError('Error reading file');
                setJsonData(null);
            };
            
            reader.readAsText(file);
        }
    };

    const handleUpload = () => {
        if (jsonData) {
            onUpload(jsonData);
            // Reset the state
            setJsonData(null);
            setFileName('');
            setError('');
        }
    };

    return (
        <Modal
            visible={visible}
            onDismiss={onDismiss}
            header="Select Light Configuration File"
            footer={
                <SpaceBetween direction="horizontal" size="xs">
                    <Button
                        key="cancel-button"
                        variant="link" 
                        onClick={onDismiss}
                    >
                        Cancel
                    </Button>
                    <Button
                        key="upload-button"
                        variant="primary" 
                        onClick={handleUpload} 
                        disabled={!jsonData}
                    >
                        Upload
                    </Button>
                </SpaceBetween>
            }
        >
            <SpaceBetween size="m">
                <FormField
                    key="form-field"
                    label="Select Light Configuration File"
                    description="Choose a light configuration file to upload"
                >
                    <div
                        key="file-input-container"
                        style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                    >
                        <input
                            key="file-input"
                            type="file"
                            accept=".wlpc"
                            id="jsonFileInput"
                            style={{ display: 'none' }}
                            onChange={handleFileSelect}
                        />
                        <Button
                            key="choose-file-button"
                            onClick={() => document.getElementById('jsonFileInput')?.click()}
                        >
                            Choose File
                        </Button>
                        {fileName && (
                            <span key="filename-display">{fileName}</span>
                        )}
                    </div>
                </FormField>
                <div key="alert-container">
                    {error && (
                        <Alert
                            type="error"
                        >
                            {error}
                        </Alert>
                    )}
                </div>
            </SpaceBetween>
        </Modal>
    );
};

export default UploadModal;
